/**
 * User preference: app background shade (dark / mid / light).
 * Stored in localStorage; applies to the main layout bg, body, and glass surface tokens.
 * Default is "mid" — split difference between stone-950 and stone-900.
 */

export type AppBackground = "dark" | "mid" | "light"

export const APP_BG_STORAGE_KEY = "luster_app_bg"
export const APP_BG_CHANGE_EVENT = "luster_app_bg_change"

export const APP_BG_CLASSES: Record<AppBackground, string> = {
  dark:  "bg-[#080706]",
  mid:   "bg-[#141210]",
  light: "bg-stone-800",
}

export interface GlassTokens {
  modalBg: string
  cardBg: string
  sectionBg: string
  overlay: string
  trackBg: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  border: string
  borderLight: string
}

export const APP_GLASS_TOKENS: Record<AppBackground, GlassTokens> = {
  dark: {
    modalBg:       "bg-black/92",
    cardBg:        "bg-black/55",
    sectionBg:     "bg-black/45",
    overlay:       "bg-black/75",
    trackBg:       "bg-white/[0.04]",
    textPrimary:   "text-white/70",
    textSecondary: "text-white/45",
    textTertiary:  "text-white/30",
    border:        "border-white/[0.05]",
    borderLight:   "border-white/[0.03]",
  },
  mid: {
    modalBg:       "bg-black/85",
    cardBg:        "bg-black/40",
    sectionBg:     "bg-black/30",
    overlay:       "bg-black/60",
    trackBg:       "bg-white/[0.06]",
    textPrimary:   "text-white/80",
    textSecondary: "text-white/60",
    textTertiary:  "text-white/40",
    border:        "border-white/[0.08]",
    borderLight:   "border-white/[0.06]",
  },
  light: {
    modalBg:       "bg-black/65",
    cardBg:        "bg-black/40",
    sectionBg:     "bg-black/35",
    overlay:       "bg-black/40",
    trackBg:       "bg-white/[0.10]",
    textPrimary:   "text-white/95",
    textSecondary: "text-white/75",
    textTertiary:  "text-white/55",
    border:        "border-white/[0.12]",
    borderLight:   "border-white/[0.10]",
  },
}

const DEFAULT: AppBackground = "mid"

export function getAppBackground(): AppBackground {
  if (typeof window === "undefined") return DEFAULT
  const raw = localStorage.getItem(APP_BG_STORAGE_KEY)
  if (raw === "dark" || raw === "mid" || raw === "light") return raw
  return DEFAULT
}

export function setAppBackground(value: AppBackground): void {
  localStorage.setItem(APP_BG_STORAGE_KEY, value)
  window.dispatchEvent(new CustomEvent(APP_BG_CHANGE_EVENT, { detail: value }))
}
